import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { meetingRoomsApi, MyBooking } from "@/lib/api/client";
import { Alert, Button, Card, Badge, Tabs, Text, Title, Stack, Group, Box } from "@mantine/core";
import { MapPin, Clock, Trash2, CalendarPlus, User, Banknote } from "lucide-react";
import { MeetingNav } from "@/components/meeting-nav";
import { bookingChargeText, lateCancelNote } from "@/lib/meeting-room-pricing";
import { MyBookingCosts } from "@/components/invoices/booking-costs";
import { toast } from "sonner";
import { bookingApprovalsApi, type BookingApproval } from "@/lib/api/booking-approvals";
import { approvalBadge, approvalIdFromPath, bookingCancelAction, decisionError } from "@/lib/booking-approvals";
import { BOOKING_APPROVALS_KEY, useBookingApprovals } from "@/hooks/use-booking-approvals";
import { ApprovalRequestsCard } from "@/components/booking-approvals/approval-requests-card";
import { usePortalEvent } from "@/lib/portal-socket";
import { clock, dayClock } from "@/lib/clock";

function BookingCard({ booking, onCancel }: { booking: MyBooking; onCancel: (id: number, withdraw: boolean) => void }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const dateStr = booking.start.slice(0, 10);
  const badge = approvalBadge(booking.approval_state, booking.approval_expires_at, booking.start, booking.site_time_zone);
  const waiting = booking.approval_state === "pending";
  const cancelAction = bookingCancelAction(booking);
  // While a request waits for a decision it isn't booked yet, so the late-cancellation window doesn't apply.
  const lateNote = waiting ? null : lateCancelNote(booking.pricing, booking.start);
  const chargeText = bookingChargeText(booking);

  return (
    <Card
      padding="md"
      withBorder
      style={{ cursor: "pointer", overflow: "hidden" }}
      onClick={() => navigate(`/meeting-rooms/${booking.room_id}?date=${dateStr}`)}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap">
          <Box miw={0}>
            <Text fw={500} truncate="end">{booking.title}</Text>
            <Text fz="sm" c="dimmed" truncate="end">{booking.room_name}</Text>
            {booking.description && (
              <Text fz="xs" c="dimmed" truncate="end" mt={2}>{booking.description}</Text>
            )}
          </Box>
          {!booking.booked_by_me && (
            <Badge variant="outline" size="sm" style={{ flexShrink: 0 }}>Colleague</Badge>
          )}
        </Group>

        {badge && (
          <Badge color={badge.color} variant="light" size="sm" data-testid="approval-badge">
            {badge.label}
          </Badge>
        )}

        {!booking.booked_by_me && booking.organizer_name && (
          <Group gap={4} wrap="nowrap">
            <User style={{ height: 14, width: 14, flexShrink: 0 }} />
            <Text fz="xs" c="dimmed">{booking.organizer_name}</Text>
          </Group>
        )}

        <Group gap={4} wrap="nowrap">
          <MapPin style={{ height: 14, width: 14, flexShrink: 0 }} />
          <Text fz="xs" c="dimmed">{booking.floor_name} · {booking.address_name}</Text>
        </Group>

        <Group gap={4} wrap="nowrap">
          <Clock style={{ height: 14, width: 14, flexShrink: 0 }} />
          <Text fz="xs" c="dimmed">{dayClock(booking.start, booking.site_time_zone)} – {clock(booking.end, booking.site_time_zone)}</Text>
        </Group>

        {chargeText && (
          <Group gap={4} wrap="nowrap" data-testid="booking-charge">
            <Banknote style={{ height: 14, width: 14, flexShrink: 0, color: "var(--mantine-color-orange-text)" }} />
            <Text fz="sm" fw={600} c="orange">{chargeText}</Text>
          </Group>
        )}

        {cancelAction && (
          <Box pt={4} onClick={(e) => e.stopPropagation()}>
            {lateNote && (
              <Text fz="xs" c="red" mb={4}>{lateNote}</Text>
            )}
            {confirming ? (
              <Group gap="xs" align="center" wrap="nowrap">
                <Text fz="xs" c="dimmed">{waiting ? "Withdraw this request? It costs nothing." : "Cancel this booking?"}</Text>
                <Button
                  size="xs"
                  color="red"
                  onClick={() => onCancel(booking.id, waiting)}
                >
                  {waiting ? "Yes, withdraw" : "Yes, cancel"}
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setConfirming(false)}
                >
                  Keep
                </Button>
              </Group>
            ) : (
              <Button
                size="xs"
                variant="outline"
                color="red"
                leftSection={<Trash2 style={{ height: 14, width: 14 }} />}
                onClick={() => setConfirming(true)}
              >
                {waiting ? "Withdraw request" : "Cancel booking"}
              </Button>
            )}
          </Box>
        )}
      </Stack>
    </Card>
  );
}

export default function MyBookingsPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const focusId = approvalIdFromPath(location.pathname);
  const approvals = useBookingApprovals();
  const [busyId, setBusyId] = useState<number | null>(null);
  usePortalEvent("my_bookings_changed", () => queryClient.invalidateQueries({ queryKey: ["my-bookings"] }));

  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => meetingRoomsApi.getMyBookings(),
    staleTime: 0,
    refetchInterval: 60_000,
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, withdraw }: { id: number; withdraw: boolean }) => meetingRoomsApi.cancelBooking(id, { withdraw }),
    onSuccess: (_data, { withdraw }) => {
      toast.success(withdraw ? "Request withdrawn" : "Booking cancelled");
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["meeting-rooms"] });
    },
    onError: (err) => {
      toast.error(decisionError(err)); // e.g. "This request was just approved — cancel it as a booking."
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
    },
  });

  const decide = useMutation({
    mutationFn: ({ request, verb, note }: { request: BookingApproval; verb: "approve" | "reject"; note?: string }) =>
      verb === "approve" ? bookingApprovalsApi.approve(request.id) : bookingApprovalsApi.reject(request.id, note ?? ""),
    onMutate: ({ request }) => setBusyId(request.id),
    onSuccess: (_res, { verb }) => toast.success(verb === "approve" ? "Booking approved" : "Booking request rejected"),
    onError: (err) => toast.error(decisionError(err)),
    onSettled: () => {
      setBusyId(null);
      queryClient.invalidateQueries({ queryKey: BOOKING_APPROVALS_KEY });
      queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
    },
  });

  const bookings = data?.data ?? [];
  const mine = bookings.filter((b) => b.booked_by_me);
  const colleagues = bookings.filter((b) => !b.booked_by_me);
  const requests = approvals.data?.results ?? [];
  const requestsTotal = approvals.data?.total ?? requests.length;
  // Only when the whole list is here can a missing request be called decided (a later page may hold it, #434).
  const focusGone =
    focusId !== null &&
    approvals.isAdmin &&
    approvals.isSuccess &&
    requestsTotal <= requests.length &&
    !requests.some((r) => r.id === focusId);

  return (
    <Box p="md">
      <Title order={2} mb="md">Meeting Rooms</Title>
      <MeetingNav />
      <Tabs defaultValue="upcoming" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="upcoming">Upcoming</Tabs.Tab>
          <Tabs.Tab value="past">Past</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="upcoming">
          <Stack gap="lg">

            {focusGone && (
              <Alert color="gray" variant="light">This booking request has already been decided or withdrawn.</Alert>
            )}
            {approvals.isAdmin && (
              <ApprovalRequestsCard
                requests={requests}
                total={requestsTotal}
                focusId={focusId}
                busyId={busyId}
                onApprove={(request) => decide.mutate({ request, verb: "approve" })}
                onReject={(request, note) => decide.mutate({ request, verb: "reject", note })}
              />
            )}

            {isLoading && <Text fz="sm" c="dimmed">Loading...</Text>}
            {isError && <Text fz="sm" c="red">Failed to load bookings.</Text>}

            {!isLoading && !isError && bookings.length === 0 && (
              <Stack align="center" gap="md" py="xl" ta="center">
                <Box p={16} style={{ borderRadius: "50%", backgroundColor: "var(--mantine-color-default-border)" }}>
                  <CalendarPlus style={{ height: 32, width: 32, color: "var(--mantine-color-dimmed)" }} />
                </Box>
                <Box>
                  <Text fw={500}>No upcoming bookings</Text>
                  <Text fz="sm" c="dimmed" mt={4}>Book a meeting room for your next session</Text>
                </Box>
                <Button component={Link} to="/meeting-rooms">Browse meeting rooms</Button>
              </Stack>
            )}

            {mine.length > 0 && (
              <Stack gap="sm">
                <Text fz="sm" fw={500} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Your bookings</Text>
                {mine.map((b) => (
                  <BookingCard key={b.id} booking={b} onCancel={(id, withdraw) => cancelMutation.mutate({ id, withdraw })} />
                ))}
              </Stack>
            )}

            {colleagues.length > 0 && (
              <Stack gap="sm">
                <Text fz="sm" fw={500} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Company bookings</Text>
                {colleagues.map((b) => (
                  <BookingCard key={b.id} booking={b} onCancel={(id, withdraw) => cancelMutation.mutate({ id, withdraw })} />
                ))}
              </Stack>
            )}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="past">
          <Stack gap="sm">
            <Text fz="sm" c="dimmed">
              Your bookings that were charged — booked time, no-shows and late cancellations. Amounts exclude VAT.
            </Text>
            <MyBookingCosts />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}
